import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui/Button";
import { addToHome, homeKey, homeQuery } from "../home/queries";
import { meQuery } from "../../api/queries";
import { useOrg } from "../org/OrgProvider";
import { claimDevice } from "./claim/claimDevice";
import { claimErrorKey } from "./claim/claimError";
import { parseLabel } from "./claim/label";
import { QrScanner } from "./claim/QrScanner";
import { spacePath, spacesInTreeOrder, spacesQuery, type Device } from "./queries";

/**
 * Add a device (WEB-F-DEV-02): scan or type its label, give it a name and a space, claim it; then "Add it to the home
 * page?" (specs/home-page HOME-05), with Add the default only while the home page is empty.
 */
export function AddDeviceDialog({
  open,
  onClose,
  onAdded,
}: {
  open: boolean;
  onClose: () => void;
  onAdded: (d: Device) => void;
}) {
  const { t, i18n } = useTranslation();
  const { orgId, current, switchOrg } = useOrg();
  // No organisation of their own (none, or a Guest elsewhere only): the device goes to their personal organisation,
  // made by the claim (UR-10, bug log A11), so there is no space to pick nor a home to ask about here.
  const ownOrg = !!current && current.role !== "guest";
  const queryClient = useQueryClient();
  const dialog = useRef<HTMLDialogElement>(null);
  const ids = { label: useId(), labelHelp: useId(), name: useId(), space: useId() };
  const [label, setLabel] = useState("");
  const [name, setName] = useState("");
  const [spaceId, setSpaceId] = useState("");
  const [scanning, setScanning] = useState(false);
  const [labelError, setLabelError] = useState<string>();
  const spaces = useQuery({ ...spacesQuery(orgId ?? ""), enabled: open && ownOrg });
  const home = useQuery({ ...homeQuery(orgId ?? "", i18n.language), enabled: open && ownOrg });
  const [added, setAdded] = useState<Device>();

  const claim = useMutation({
    mutationFn: claimDevice,
    onSuccess: async (device) => {
      if (!ownOrg) {
        const me = await queryClient.fetchQuery({ ...meQuery(), staleTime: 0 });
        const personal = me.memberships.find((m) => m.org_kind === "personal" && m.role === "owner");
        if (personal) switchOrg(personal.org_id);
        onAdded(device);
        onClose();
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ["org", orgId, "/v1/devices"] });
      void queryClient.invalidateQueries({ queryKey: ["org", orgId, "alarm-devices"] });
      onAdded(device);
      setAdded(device);
    },
  });
  const answer = useMutation({
    mutationFn: (id: string) => addToHome(orgId ?? "", i18n.language, [id]),
    onSuccess: (next) => {
      queryClient.setQueryData(homeQuery(orgId ?? "", i18n.language).queryKey, next);
      onClose();
    },
  });

  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (open && !d.open) {
      setLabel("");
      setName("");
      setSpaceId("");
      setLabelError(undefined);
      setScanning(false);
      setAdded(undefined);
      claim.reset();
      answer.reset();
      d.showModal();
    } else if (!open && d.open) d.close();
    // claim.reset and answer.reset are stable; resetting on every open is the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onScan = useCallback((text: string) => {
    setLabel(text);
    setScanning(false);
    setLabelError(undefined);
  }, []);

  function checkLabel(value: string): boolean {
    try {
      parseLabel(value);
      setLabelError(undefined);
      return true;
    } catch (e) {
      setLabelError(t(claimErrorKey(e).key));
      return false;
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!checkLabel(label) || !name.trim()) return;
    claim.mutate({ label, name, spaceId: ownOrg ? spaceId || null : null, ownOrg });
  }

  const err = claim.error ? claimErrorKey(claim.error) : undefined;
  const ordered = spaces.data ? spacesInTreeOrder(spaces.data) : [];
  // Add is the default only while nothing is on the home page yet, so a home someone arranged is never changed by
  // pressing Enter.
  const homeWidgets = (home.data?.document as { widgets?: unknown[] } | undefined)?.widgets;
  const homeEmpty = !!home.data && (homeWidgets?.length ?? 0) === 0;
  const addedName = added?.name ?? added?.serial ?? "";

  return (
    <dialog
      ref={dialog}
      onClose={onClose}
      aria-labelledby={`${ids.label}-title`}
      className="m-auto w-full max-w-lg rounded-lg p-0 shadow-xl backdrop:bg-black/40"
    >
      {added ? (
        <div className="p-6">
          <h2 id={`${ids.label}-title`} className="text-lg font-semibold text-brand">
            {t("addDevice.homeTitle", { name: addedName })}
          </h2>
          <p className="mt-3 text-sm text-gray-900">{t("addDevice.homeText")}</p>
          {answer.isError && (
            <p role="alert" className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-900">
              {t("addDevice.homeFailed")}
            </p>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <Button
              variant={homeEmpty ? "secondary" : "primary"}
              autoFocus={!homeEmpty}
              disabled={answer.isPending}
              onClick={() => {
                // Not now records nothing: the device waits in the home page's hint (specs/home-page HOME-06).
                void queryClient.invalidateQueries({ queryKey: homeKey(orgId ?? "") });
                onClose();
              }}
            >
              {t("addDevice.homeNotNow")}
            </Button>
            <Button
              variant={homeEmpty ? "primary" : "secondary"}
              autoFocus={homeEmpty}
              disabled={answer.isPending}
              onClick={() => answer.mutate(added.id)}
            >
              {t("addDevice.homeAdd")}
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="p-6" noValidate>
          <h2 id={`${ids.label}-title`} className="text-lg font-semibold text-brand">
            {t("addDevice.title")}
          </h2>

          <label htmlFor={ids.label} className="mt-4 block text-sm font-medium">
            {t("addDevice.label")}
          </label>
          <p id={ids.labelHelp} className="text-xs text-gray-700">
            {t("addDevice.labelHelp")}
          </p>
          <div className="mt-1 flex gap-2">
            <input
              id={ids.label}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={() => label && checkLabel(label)}
              aria-describedby={ids.labelHelp}
              aria-invalid={!!labelError}
              autoComplete="off"
              spellCheck={false}
              className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs"
              placeholder="PV1:…"
            />
            <Button variant="secondary" onClick={() => setScanning((s) => !s)} aria-pressed={scanning}>
              {scanning ? t("addDevice.stopScan") : t("addDevice.scan")}
            </Button>
          </div>
          {labelError && (
            <p role="alert" className="mt-1 text-sm text-red-800">
              {labelError}
            </p>
          )}
          {scanning && <QrScanner onResult={onScan} />}

          <label htmlFor={ids.name} className="mt-4 block text-sm font-medium">
            {t("addDevice.name")}
          </label>
          <input
            id={ids.name}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={200}
            className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          />

          {ownOrg && (
            <>
              <label htmlFor={ids.space} className="mt-4 block text-sm font-medium">
                {t("addDevice.space")}
              </label>
              <select
                id={ids.space}
                value={spaceId}
                onChange={(e) => setSpaceId(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
              >
                <option value="">{t("addDevice.noSpace")}</option>
                {ordered.map((s) => (
                  <option key={s.id} value={s.id}>
                    {spacePath(spaces.data ?? [], s.id)}
                  </option>
                ))}
              </select>
            </>
          )}

          {err && (
            <p role="alert" className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-900">
              {t(err.key, { minutes: err.minutes })}
            </p>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={claim.isPending || !label || !name.trim()}>
              {claim.isPending ? t("addDevice.adding") : t("addDevice.add")}
            </Button>
          </div>
        </form>
      )}
    </dialog>
  );
}
